import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Code } from '../CodeSample';
import { GuideHeader } from './GuideHeader';
import { destinations, packageExample } from './resultsData';

/** The document, its fields and evidence, the package, and the destinations results go to. */
export function ResultsGuide(): ReactNode {
  return (
    <article className="docs-page">
      <GuideHeader slug="results" />
      <Fields />
      <Package />
      <Destinations />
    </article>
  );
}

function Fields(): ReactNode {
  return (
    <section className="docs-section" aria-labelledby="fields-title">
      <h2 id="fields-title">A document’s fields</h2>
      <p>
        <code>GET /documents/{'{documentId}'}</code> answers the document with its{' '}
        <code>fields</code>. Each has its <code>name</code> in the job, its <code>value</code> as
        text, a <code>confidence</code> from 0 to 1, and where it was read: <code>page_id</code>,
        one of the document’s <code>pageIds</code>, and <code>evidence</code>, a box in fractions of
        the page’s width and height from its top-left corner. On a page 1,236 pixels wide,{' '}
        <code>x: 0.1375</code> is 170 pixels in. A value Cortexa did not tie to a place has both
        null, and so has a value a person confirmed and every value of a document that came in by
        import, email, scanner or watched folder. The keys inside a field are snake_case, as in{' '}
        <code>document.json</code> and the webhook body.
      </p>
      <Values />
      <p>
        Table rows are in <code>lineItems</code>, in the document’s own order: <code>row</code>,{' '}
        <code>description</code>, <code>quantity</code>, <code>unit_price</code>,{' '}
        <code>amount</code>, <code>confidence</code> and <code>page_number</code>. A document read
        again after a person corrects it gets a new <code>revision</code>, and that revision is
        exported and delivered again.
      </p>
    </section>
  );
}

/** What a value is on each kind of job, and that the API, the package and the webhook agree. */
function Values(): ReactNode {
  return (
    <>
      <p>
        A <code>value</code> is always text and is never reformatted by Paige, so parse dates and
        amounts on your side. A job is read with its own setup, and answers each value as the page
        prints it: <code>01/10/2026</code>, <code>$24,021.59</code>, <code>1,204.50</code>. A field
        nothing was read for has a null or an empty <code>value</code>.
      </p>
      <p>
        The values are the ones the document was exported with: for a <code>revision</code>, this
        answer, <code>document.json</code> in its package and the webhook body carry the same{' '}
        <code>fields</code> and the same <code>lineItems</code>. While a document waits for a person
        they are the values as Cortexa read them, and <code>check.issues</code> says why it waits.
        This holds however the pages arrived: an upload through the API, an import into a job, an
        email, a scanner or a watched folder.
      </p>
    </>
  );
}

function Package(): ReactNode {
  return (
    <section className="docs-section" aria-labelledby="package-title">
      <h2 id="package-title">The package</h2>
      <p>
        When a document is exported its package is written, and both files download from{' '}
        <code>
          GET /documents/{'{documentId}'}/package/{'{file}'}
        </code>{' '}
        under the name the job’s file naming gives them. <code>pages.pdf</code> is a searchable
        PDF/A-2: each page’s image with the text Cortexa read laid invisibly over it, the file name
        as its title, the type as its subject, and every field in its metadata (XMP, under{' '}
        <code>https://v2.paige.app/ns/fields/1.0/</code>). <code>document.json</code> is the same
        revision as data: the pages with the file and page each came from, the fields, the table
        rows, and <code>filed</code>, the folders and name it was filed under.
      </p>
      <Code code={packageExample} label="document.json" />
    </section>
  );
}

function Destinations(): ReactNode {
  return (
    <section className="docs-section" aria-labelledby="destinations-title">
      <h2 id="destinations-title">Where results go</h2>
      <p>
        Every confirmed document is packaged for download and then delivered to each of the job’s
        destinations (<code>GET /jobs/{'{jobId}'}/destinations</code>). A destination added later
        can be sent what it missed with <code>POST .../backfill</code>.
      </p>
      <table className="docs-table">
        <thead>
          <tr>
            <th scope="col">Destination</th>
            <th scope="col">What it gets</th>
          </tr>
        </thead>
        <tbody>
          {destinations.map((d) => (
            <tr key={d.name}>
              <th scope="row">{d.name}</th>
              <td>{d.gets}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p>
        Webhooks have a guide of their own:{' '}
        <Link to="/developers/$guide" params={{ guide: 'webhooks' }}>
          Webhooks
        </Link>
        .
      </p>
    </section>
  );
}
